import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { api, type Business } from '@/lib/api';
import { Button } from '@/components/ui/Button';

/**
 * Resolves `/crm` to a concrete `/crm/:businessId/overview` URL.
 *
 * Without this the CRM nav link bounces to landing: `/crm` → index Navigate
 * to "overview" → `/crm/overview` → matches `/crm/:businessId`, then the
 * child index Navigate becomes `/crm/overview/overview` which doesn't match
 * anything and falls through to "/".
 */
export default function CrmIndexRedirect() {
  const navigate = useNavigate();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'empty' }
    | { kind: 'error'; message: string }
    | { kind: 'go'; to: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { businesses } = await api.listBusinesses();
        if (cancelled) return;
        if (businesses.length === 0) {
          setState({ kind: 'empty' });
          return;
        }
        // Prefer the most recently live business; fall back to first record.
        const target =
          businesses.find((b: Business) => b.status === 'live') || businesses[0];
        setState({ kind: 'go', to: `/crm/${target.id}/overview` });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to load',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state.kind === 'go') return <Navigate to={state.to} replace />;
  if (state.kind === 'loading') {
    return <div className="text-zinc-500 text-sm">Opening CRM…</div>;
  }
  if (state.kind === 'empty') {
    return (
      <div className="grid place-items-center py-20 text-center">
        <div className="rounded-full bg-cyan-500/10 p-3 ring-4 ring-cyan-500/20">
          <Users className="size-7 text-cyan-300" />
        </div>
        <h2 className="mt-5 text-2xl font-semibold">No businesses yet</h2>
        <p className="mt-1 text-zinc-400">
          Finish onboarding to start collecting customers and jobs.
        </p>
        <div className="mt-6">
          <Link to="/onboarding/new">
            <Button size="lg">Start onboarding</Button>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="text-sm text-red-400">
      Couldn’t open the CRM: {state.message}
    </div>
  );
}
